import { clone, isEmpty, isNil } from "ramda";
import {
  getAlertAdditionalInfo,
  getAnalysisSpatialData,
  getAssetPointsAdditionalData,
  getAssetPointsSerialData,
  getAssetsDates,
  getAssetsDatetimes,
  getChunkInfo,
  getDoBData,
  getDoBDataId,
  getPointsByAssets,
  getReport,
} from "../../api";
import { toast } from "react-toastify";
import { BOOLEAN_MEASURES } from "../../utils/consts";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PhotoIcon from "@mui/icons-material/Photo";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import { parseCSV } from "../../utils/utils";

export const loadData = async ({
  asset,
  selectedEquipment,
  dateTimeList,
  points,
  data,
  equipment_ID,
  channel,
  asignedChannels,
  selectedSub,
  assetModule,
  subList,
  measurandsFiltered,
  setData,
  setBooleanData,
  setLoading,
  setPercentiles,
  setDifferences,
  dobDates,
  setDobDates,
}) => {
  let noData = false;
  setLoading(true);
  try {
    const result = [];
    const resultBoolean = [];
    let minPercentiles = null;
    let maxPercentiles = null;
    let differences = null;
    let type = "";
    let spatialDataResponse = null;
    let dobFiles = null;
    const allDob = [];
    let newDobDates = [];
    for (let i = 0; i < dateTimeList.length; i++) {
      const date = dateTimeList[i][0];
      const actualChannel = dateTimeList[i][1];
      const actualSub = dateTimeList[i][3];
      const actualPoints = points[`${asset.name}_${actualSub.polyline}_${selectedEquipment.equipmentType}`];
      if (measurandsFiltered.includes("DoB")) {
        const dobData = {
          assets: [asset.name],
          equipmentsIds: [equipment_ID],
          substructures: [actualSub.ref],
        };
        dobFiles = await getDoBData(dobData);
        dobFiles = dobFiles.data.data;
      }

      spatialDataResponse = await getAnalysisSpatialData(
        asset.name,
        date,
        equipment_ID,
        actualChannel,
        actualSub,
        assetModule,
      );
      if (
        !isNil(spatialDataResponse?.status) &&
        spatialDataResponse.status === 200 &&
        !isNil(spatialDataResponse.data)
      ) {
        const spatialData = spatialDataResponse.data.data;
        if (isEmpty(spatialData)) {
          noData = true;
          setData({});
          setLoading(false);
        } else {
          type = spatialData.substructure_type;
          const isGeneric = !type || type === "generic";

          minPercentiles = isGeneric ? {} : [];
          maxPercentiles = isGeneric ? {} : [];
          differences = isGeneric ? {} : [];
          if (isGeneric) {
            for (let indexMeasurement = 0; indexMeasurement < measurandsFiltered.length; indexMeasurement++) {
              const measurand = measurandsFiltered[indexMeasurement];
              if (!isNil(spatialData.boundaries[measurand]) && !isNil(spatialData.differences[measurand])) {
                if (!isNil(minPercentiles[measurand])) {
                  minPercentiles[measurand].push(spatialData.boundaries[measurand][0]);
                } else {
                  minPercentiles[measurand] = [spatialData.boundaries[measurand][0]];
                }
                if (!isNil(maxPercentiles[measurand])) {
                  maxPercentiles[measurand].push(spatialData.boundaries[measurand][1]);
                } else {
                  maxPercentiles[measurand] = [spatialData.boundaries[measurand][1]];
                }
                if (!isNil(differences[measurand])) {
                  differences[measurand].push(spatialData.differences[measurand]);
                } else {
                  differences[measurand] = [spatialData.differences[measurand]];
                }
                if (measurand === "DoB") {
                  spatialData.boundaries["DoB"] = spatialData.boundaries["temperature"];
                  spatialData.differences["DoB"] = spatialData.differences["temperature"];
                }
              }
            }
          } else if (type === "inclinometer") {
            for (const axisKey of spatialData.axis_keys ?? []) {
              // Inicializar eje si no existe
              if (!minPercentiles[axisKey]) minPercentiles[axisKey] = {};
              if (!maxPercentiles[axisKey]) maxPercentiles[axisKey] = {};
              if (!differences[axisKey]) differences[axisKey] = {};

              for (const measurand of measurandsFiltered) {
                const boundary = spatialData.boundaries?.[axisKey]?.[measurand];
                const diffValue = spatialData.differences?.[axisKey]?.[measurand];

                if (!isNil(boundary) && !isNil(diffValue)) {
                  //MIN
                  if (!minPercentiles[axisKey][measurand]) {
                    minPercentiles[axisKey][measurand] = [];
                  }
                  minPercentiles[axisKey][measurand].push(boundary[0]);

                  //MAX
                  if (!maxPercentiles[axisKey][measurand]) {
                    maxPercentiles[axisKey][measurand] = [];
                  }
                  maxPercentiles[axisKey][measurand].push(boundary[1]);

                  //DIFF
                  if (!differences[axisKey][measurand]) {
                    differences[axisKey][measurand] = [];
                  }
                  differences[axisKey][measurand].push(diffValue);
                }
              }
            }
          }

          const newItem = {};
          const newItemBoolean = {};
          if (isGeneric) {
            newItem["date"] = date;
            if (selectedEquipment.equipmentType === "A-DTS") {
              const aDTSDatetimes = [];
              const aDTSminutes = [];
              const dateFormatted = new Date(date);
              for (let indexDate = 0; indexDate < spatialData.timestamp.length; indexDate++) {
                const newDate = new Date(dateFormatted.getTime() + spatialData.timestamp[indexDate] * 1000);
                aDTSDatetimes.push(newDate.toISOString().substring(0, newDate.toISOString().indexOf(".")));
                aDTSminutes.push(spatialData.timestamp[indexDate] / 60);
              }
              newItem["datetimes"] = aDTSDatetimes;
              newItem["minutes"] = aDTSminutes;
              newItem["temperature"] = spatialData.temperature_matrix;
              newItem["current_intensity"] = spatialData.currents;
              newItem["voltage"] = spatialData.voltages;
            }
            newItem["pk"] = actualPoints?.points.map((p) => p.pk);
            newItemBoolean["date"] = date;
            newItemBoolean["pk"] = actualPoints?.points.map((p) => p.pk);
            for (let j = 0; j < measurandsFiltered.length; j++) {
              const measureName = measurandsFiltered[j];
              let measurePk = null;
              let coordsPk = null;
              let coordsSeabed = null;
              let dobId = null;
              let values = BOOLEAN_MEASURES.includes(measureName)
                ? spatialData.additional_measures?.[measureName]
                : spatialData[measureName];
              if (measureName === "DoB" && isNil(values) && i === 0 && !isNil(dobFiles) && !isEmpty(dobFiles)) {
                let dobData = null;
                let done = false;
                //si tiene Don
                newDobDates = Object.keys(dobFiles[asset.name][equipment_ID][actualSub.ref][assetModule]["DoB"]);

                for (let indexDobKey = 0; indexDobKey < newDobDates.length; indexDobKey++) {
                  for (
                    let indexDobDate = 0;
                    indexDobDate <
                    dobFiles[asset.name][equipment_ID][actualSub.ref][assetModule]["DoB"][newDobDates[indexDobKey]]
                      .length;
                    indexDobDate++
                  ) {
                    const dobs = await getDoBDataId(
                      dobFiles[asset.name][equipment_ID][actualSub.ref][assetModule]["DoB"][newDobDates[indexDobKey]][
                        indexDobDate
                      ].id,
                    );
                    dobData = parseCSV(dobs.data);

                    if (!done) {
                      measurePk = dobData.map((d) => d.pk * 1000);
                      coordsSeabed = dobData.map((d) => -d.seabed);
                      dobId =
                        dobFiles[asset.name][equipment_ID][actualSub.ref][assetModule]["DoB"][newDobDates[indexDobKey]][
                          indexDobDate
                        ].id;
                      const dobValues = dobData.map((d) => -d.dob);
                      values = dobValues;
                      coordsPk = dobData.map((d) => [d.latitude, d.longitude]);
                      done = true;
                    }

                    const newDob = {
                      id: dobFiles[asset.name][equipment_ID][actualSub.ref][assetModule]["DoB"][
                        newDobDates[indexDobKey]
                      ][indexDobDate].id,
                      datetime: newDobDates[indexDobKey] + "T",
                      values: dobData.map((element) => -element.dob),
                      channel: channel,
                      coordinates: dobData.map((element) => [element.latitude, element.longitude]),
                      pk: dobData.map((element) => element.pk * 1000),
                      seabed: dobData.map((element) => -element.seabed),
                    };
                    allDob.push(newDob);
                  }
                }
              }
              if (BOOLEAN_MEASURES.includes(measureName) && !isNil(values)) {
                newItemBoolean[measureName] = {
                  datetime: date,
                  values: values,
                  channel: actualChannel,
                };
              } else if (!isNil(values)) {
                newItem[measureName] = {
                  datetime: date,
                  values: values,
                  channel: actualChannel,
                  ...(measureName.includes("dob") || measureName === "DoB"
                    ? { pk: measurePk, coordinates: coordsPk, seabed: coordsSeabed, id: dobId }
                    : null),
                };
              }
            }
            result.push(newItem);
            resultBoolean.push(newItemBoolean);
          } else if (type === "inclinometer") {
            newItem["date"] = date;
            newItem["axisKeys"] = spatialData.axis_keys;
            for (let j = 0; j < measurandsFiltered.length; j++) {
              const measureName = measurandsFiltered[j];
              if (!newItem[measureName]) newItem[measureName] = {};
              for (const axisKey of spatialData.axis_keys ?? []) {
                newItem[measureName][axisKey] = {
                  datetime: date,
                  depth: spatialData[actualSub][measureName][axisKey].depth,
                  values: spatialData[actualSub][measureName][axisKey].values,
                };
              }
            }
            result.push(newItem);
          }
        }
      }
    }
    for (let indexDob = 1; indexDob < allDob.length; indexDob++) {
      const newItem = {};
      newItem["DoB"] = allDob[indexDob];
      result.push(newItem);
    }

    const newPercentiles = isNil(type) || type === "" || type === "generic" ? {} : [];
    const newDifferences = isNil(type) || type === "" || type === "generic" ? {} : [];
    if (isNil(type) || type === "" || type === "generic") {
      for (let indexMeasurement = 0; indexMeasurement < measurandsFiltered.length; indexMeasurement++) {
        const measurand = measurandsFiltered[indexMeasurement];
        if (!isNil(minPercentiles[measurand]) && !isNil(maxPercentiles[measurand]) && !isNil(differences[measurand])) {
          const min = Math.min(...minPercentiles[measurand]);
          const max = Math.max(...maxPercentiles[measurand]);
          const diff = Math.max(...differences[measurand]);
          newPercentiles[measurand] = [min, max];
          newDifferences[measurand] = diff;
        }
      }
    } else if (type === "inclinometer") {
      for (let axisIndex = 0; axisIndex < spatialDataResponse?.data.data.axis_keys.length; axisIndex++) {
        const axisKey = spatialDataResponse?.data.data.axis_keys[axisIndex];

        if (!newPercentiles[axisKey]) newPercentiles[axisKey] = {};
        if (!newDifferences[axisKey]) newDifferences[axisKey] = {};

        for (let indexMeasurement = 0; indexMeasurement < measurandsFiltered.length; indexMeasurement++) {
          const measurand = measurandsFiltered[indexMeasurement];

          if (
            !isNil(minPercentiles[axisKey]?.[measurand]) &&
            !isNil(maxPercentiles[axisKey]?.[measurand]) &&
            !isNil(differences[axisKey]?.[measurand])
          ) {
            const min = Math.min(...minPercentiles[axisKey][measurand]);
            const max = Math.max(...maxPercentiles[axisKey][measurand]);
            const diff = Math.max(...differences[axisKey][measurand]);

            newPercentiles[axisKey][measurand] = [min, max];
            newDifferences[axisKey][measurand] = diff;
          }
        }
      }
    }
    console.log(result);
    setPercentiles(newPercentiles);
    setDifferences(newDifferences);
    setDobDates(newDobDates);
    await setData(result);
    await setBooleanData(resultBoolean);
    if (!noData) setLoading(false);
  } catch (error) {
    toast.error(error.message, { className: "toast-error" });
  }
};

export const setEquipmentAndChannelData = async ({
  assetInfo,
  subList,
  selectedSub,
  equipmentTypes,
  configData,
  moduleName,
  equipmentID,
  channelParam,
  alertsList,
  newPackage,
  param,
  localParams,
  anyTrue,
  setMeasurandsOptions,
  setMeasurandsFiltered,
  setSelectedEquipment,
  setEquipment,
  setAsignedChannels,
  setAssetEquipments,
  setChannel,
  setSubList,
  setSelectedSub,
  setEquipmentChannels,
  setFilter,
  setAssetEventList,
  setAssetEvent,
  setAssetAlerts,
  setDateTimeList,
  setAssetSensors,
  setSelectedAlert,
  setMeasurementActive,
  setNonMeasurands,
  setLoading,
  setData,
  setChannelFound,
  setDateOptions = () => {},
  setTimeOptions = () => {},
  setDateOptionsModal = () => {},
  setTimeOptionsModal = () => {},
  setDate = () => {},
  setTime = () => {},
}) => {
  let auxID = null;
  const newEquipments = [];

  for (let indexEquipment = 0; indexEquipment < assetInfo.equipments.length; indexEquipment++) {
    const actualEquipment = assetInfo.equipments[indexEquipment];
    const equipmentData = equipmentTypes.find((et) => et.name === actualEquipment.equipmentType);
    const moduleFound = equipmentData.modules.find((mdl) => mdl.module === moduleName);
    if (!isNil(moduleFound)) {
      const actualModule = assetInfo.config.modules.find((module) => module.name === moduleName);
      const packagesFound = actualModule.packages;

      const allPackagesFound = moduleFound.packages.some((packageName) =>
        packagesFound.some((pkg) => pkg.name === packageName),
      );
      if (isNil(auxID) && allPackagesFound) {
        auxID = actualEquipment.equipmentID;
      }
      if (allPackagesFound) newEquipments.push(actualEquipment);
    }
  }
  const equipmentInit = !isNil(equipmentID) ? equipmentID : auxID;
  const equipment = assetInfo.equipments.find((item) => item.equipmentID === equipmentInit);

  const newMeasurands = [];
  const nonMeasurands = [];
  for (let indexMeasurand = 0; indexMeasurand < assetInfo.config?.modules?.length; indexMeasurand++) {
    const actualModule = assetInfo.config.modules[indexMeasurand];
    if (actualModule.name === moduleName) {
      const auxNonPack = actualModule.packages.filter((pack) => !pack.measurand);
      const newPackages = actualModule.packages.map((pack) => pack.name);
      newMeasurands.push(...newPackages);
      nonMeasurands.push(...auxNonPack);
    }
  }
  const noRepeating = [...new Set(newMeasurands)];
  if (!isEmpty(noRepeating) && !param && anyTrue) {
    const finalNonMeasurands = [];
    for (let indexVar = 0; indexVar < nonMeasurands.length; indexVar++) {
      if (!isNil(nonMeasurands[indexVar].extraVars) && !isNil(nonMeasurands[indexVar].extraVars.computed)) {
        finalNonMeasurands.push(nonMeasurands[indexVar].name);
      }
    }
    setNonMeasurands(nonMeasurands);
    setMeasurementActive(true);
    setFilter("measurands");
    setAssetEquipments(newEquipments);

    setSelectedEquipment(equipment);
    setEquipment(equipmentInit);
    const menuItems = [];
    for (let index = 0; index < equipment.channels.length; index++) {
      menuItems.push(index + 1);
    }

    const printChannel = equipment.channels.findIndex((channel) =>
      channel.substructures.find((sub) => channel.measurement.receiving && sub === selectedSub.ref),
    );
    if (printChannel >= 0) {
      setAsignedChannels(equipment.channels);
      const print = !isNil(channelParam) ? parseInt(channelParam) : printChannel + 1;
      setChannel(print);
      setEquipmentChannels(menuItems);
      setChannelFound(true);
    } else {
      setAsignedChannels(null);
      setChannel(null);
      setSubList(null);
      setSelectedSub(null);
      setEquipmentChannels(null);
      setChannelFound(false);
    }
    if (assetInfo.config?.additionalSensors?.active) {
      setAssetSensors(assetInfo.config.additionalSensors.sensors);
    } else {
      setAssetSensors([]);
    }

    const extraFilters = [];
    for (
      let indexMeasurand = 0;
      indexMeasurand < assetInfo.config?.additionalSensors?.measurands?.length;
      indexMeasurand++
    ) {
      const category = assetInfo.config.additionalSensors.measurands[indexMeasurand].category;
      if (!extraFilters.includes(category)) {
        extraFilters.push(category);
      }
    }
    //setear a una en el index de analysis para tener guardadas las extraVars y asi luego hacer las llamadas a alertas?
    const finalFilters = [...noRepeating, ...new Set(finalNonMeasurands), ...extraFilters];
    setSelectedAlert(null);
    noRepeating.push(...new Set(finalNonMeasurands));
    setMeasurandsOptions(finalFilters);
    setMeasurandsFiltered(noRepeating);
  } else {
    setSelectedEquipment(equipment);
    setAssetEquipments(newEquipments);
    setEquipment(equipmentInit);
    const menuItems = [];
    for (let index = 0; index < equipment.channels.length; index++) {
      menuItems.push(index + 1);
    }

    const printChannel = equipment.channels.findIndex(
      (channel) => channel.measurement.receiving && channel.substructures.find((sub) => sub === selectedSub.ref),
    );
    if (printChannel >= 0) {
      setAsignedChannels(equipment.channels);
      setChannelFound(true);
      const print = !isNil(channelParam) ? parseInt(channelParam) : printChannel + 1;
      setChannel(print);

      setEquipmentChannels(menuItems);
      setDateTimeList([]);
      const newAlertList = alertsList.measurements.filter(
        (alert) => alert.asset === assetInfo.name && alert.module === moduleName,
      );
      let actualAlert = null;
      if (!isNil(localParams))
        actualAlert = newAlertList.find(
          (aa) =>
            aa.dateStart === localParams.dateTime &&
            aa.startIndex === parseFloat(localParams.startPointIndex) &&
            aa.endIndex === parseFloat(localParams.endPointIndex),
        );
      const realSub = !isNil(actualAlert) ? actualAlert.substructure : selectedSub.ref;
      const finalAlertList = newAlertList.filter((alert) => alert.substructure === realSub);
      const auxEvents = finalAlertList.map((alert) => alert.package);
      const newEvents = auxEvents.filter((item, index) => auxEvents.indexOf(item) === index);
      const moduleFound = configData.modules.find((module) => module.name === moduleName);
      let noMeasurement = false;
      for (let indexPackage = 0; indexPackage < moduleFound?.packages?.length; indexPackage++) {
        if (newEvents.includes(moduleFound.packages[indexPackage].name)) {
          if (moduleFound.packages[indexPackage].measurand) {
            noMeasurement = true;
            break;
          }
        }
      }
      setMeasurementActive(noMeasurement);
      setAssetEventList(newEvents);
      setAssetEvent(!isNil(newPackage) ? newPackage : newEvents[0]);
      setAssetAlerts(newAlertList);
      setFilter("alerts");
    } else {
      setChannelFound(false);
      setDateOptions(null);
      setTimeOptions(null);
      setDateOptionsModal(null);
      setTimeOptionsModal(null);
      setDate(null);
      setTime(null);
      setDateTimeList([]);
      setData(null);
    }
  }
};

export const loadDateOptionsChannel = async ({
  date,
  time,
  asset,
  equipment,
  channel,
  selectedSub,
  assetInfo,
  dateTime,
  equipmentChannels,
  assetEquipments,
  dateOptions,
  setDateOptions = () => {},
  setTimeOptions = () => {},
  setDateOptionsModal = () => {},
  setTimeOptionsModal = () => {},
  setDate = () => {},
  setTime = () => {},
  setLoading = () => {},
  setDateTimeList = () => {},
  setChannel,
  setSelectedSub,
  firstTime,
  setFirstTime,
  selectedEquipment,
  setSelectedEquipment,
}) => {
  if (!isNil(asset) && !isNil(equipment) && !isNil(channel)) {
    let dates = null;
    let changedEquipment = equipment;
    let changedChannel = channel;
    let changedSub = selectedSub;
    let found = false;
    if (isNil(date)) {
      for (let indexEquipment = 0; indexEquipment < assetEquipments.length && !found; indexEquipment++) {
        if (selectedEquipment === assetEquipments[indexEquipment] || firstTime) {
          for (
            let indexChannel = 0;
            indexChannel < assetEquipments[indexEquipment].channels.length && !found;
            indexChannel++
          ) {
            if (
              (channel === indexChannel + 1 || firstTime) &&
              assetEquipments[indexEquipment].channels[indexChannel].state
            ) {
              for (
                let indexSub = 0;
                indexSub < assetEquipments[indexEquipment].channels[indexChannel].substructures.length;
                indexSub++
              ) {
                const channelSub = assetEquipments[indexEquipment].channels[indexChannel].substructures[indexSub];
                const actualSub = assetInfo.substructures.find((sub) => channelSub.includes(sub.ref));
                const response = await getAssetsDates(
                  [asset],
                  selectedEquipment.equipmentID,
                  indexChannel + 1,
                  actualSub.ref,
                );
                dates = response.data.data;

                if (!isEmpty(dates)) {
                  setFirstTime(false);
                  if (indexChannel + 1 !== channel) {
                    setChannel(indexChannel + 1);
                  }
                  setSelectedEquipment(assetEquipments[indexEquipment]);
                  changedEquipment = assetEquipments[indexEquipment].equipmentID;
                  changedChannel = indexChannel + 1;
                  changedSub = actualSub;

                  found = true;
                  break;
                }
              }
            }
          }
        }
      }
    }
    let timeResponse = null;
    if ((!isEmpty(dates) && !isNil(dates)) || !isNil(date)) {
      timeResponse = await getAssetsDatetimes(
        [asset],
        changedEquipment,
        changedChannel,
        changedSub.ref,
        !isNil(date) ? date : dates[0],
      );
    }
    if (
      !isNil(timeResponse?.status) &&
      timeResponse.status === 200 &&
      !isNil(timeResponse.data) &&
      !isEmpty(timeResponse.data.data)
    ) {
      const times = timeResponse.data.data;

      let localDateIndex = isNil(dates) ? dateOptions.indexOf(date) : 0;

      let localTimeIndex = isNil(time) ? 0 : times.indexOf(time);

      setDate(localDateIndex);
      setTime(localTimeIndex);
      setDateOptions(dates);
      setTimeOptions(times);
      if (!isNil(dates)) {
        setDateOptionsModal(dates);
      }
      setTimeOptionsModal(times);
      setLoading(false);
    } else {
      setDateOptions(null);
      setTimeOptions(null);
      setDateOptionsModal(null);
      setTimeOptionsModal(null);
      setDate(null);
      setTime(null);
      setDateTimeList([]);
      setLoading(true);
    }
  } else {
    setDateOptions(null);
    setTimeOptions(null);
    setDateOptionsModal(null);
    setTimeOptionsModal(null);
    setDate(null);
    setTime(null);
    setDateTimeList([]);
    setLoading(true);
  }
};

export const handleModalAccept = ({ date, time, modalChannel, substructure, dateTimeList, setDateTimeList }) => {
  const dateStr = `${date}T${time}`;
  if (!dateTimeList.includes(dateStr)) {
    setDateTimeList([...dateTimeList, [dateStr, modalChannel, substructure.polyline, substructure]]);
  }
};

export const initialSpatialSelectHandler = ({
  data,
  start,
  end,
  measureName,
  setTemporalDataList,
  minsWaterfall,
  setMinsWaterfall,
  maxsWaterfall,
  setMaxsWaterfall,
  currentAsset,
  spatialDataList,
  setLoading,
  dateTime,
  equipment,
  selectedEquipment,
  channel,
}) => {
  setLoading(true);
  let i = start;
  let j = end;
  if (j - i >= 64) {
    let centerPoint = parseInt((j - i) / 2) + i;
    i = centerPoint >= 32 ? centerPoint - 32 : 0;
    centerPoint += 32;
    const count = data?.series?.[0]?.data?.length ?? 0;
    j = centerPoint < count ? centerPoint : count - 1;
  }
  //initialLoadTemporalData({
  //  asset: currentAsset,
  //  startIndex: i,
  //  endIndex: j,
  //  pkList: spatialDataList?.[0]?.pk?.slice(i, j + 1),
  //  measureName,
  //  setTemporalDataList,
  //  setMinsWaterfall,
  //  setMaxsWaterfall,
  //  minsWaterfall,
  //  maxsWaterfall,
  //  setLoading,
  //  dateTime,
  //  selectedEquipment,
  //  equipment,
  //  channel,
  //});
  //setLoading(false);
};

export const initialLoadTemporalData = async ({
  asset,
  startIndex,
  endIndex,
  pkList,
  measureName,
  setTemporalDataList,
  setMinsWaterfall,
  setMaxsWaterfall,
  setLoading,
  dateTime,
  selectedEquipment,
  equipment,
  channel,
}) => {
  try {
    const result = {};
    const minsWaterfall = {};
    const maxsWaterfall = {};

    const measures = selectedEquipment.pMeasureNames.filter((m) => !BOOLEAN_MEASURES.includes(m));
    for (let mI = 0; mI < measures.length; mI++) {
      const measureName = measures[mI];
      const response = await getAssetPointsSerialData(
        asset.name,
        startIndex,
        endIndex,
        dateTime,
        [measureName],
        equipment,
        channel,
        30,
        5000,
      );
      if (!isNil(response?.status) && response.status === 200 && !isNil(response.data)) {
        const data = response.data?.data;

        let minValue = Infinity;
        let maxValue = Number.NEGATIVE_INFINITY;
        if (isNil(result[measureName])) {
          result[measureName] = [];
        }
        for (let i = 0, j = startIndex; i < data.data.length; i++, j++) {
          const item = data.data[i];
          const values = item[measureName].map((item) => item.value);
          const min = Math.min(...values);
          const max = Math.max(...values);
          if (minValue > min) minValue = min;
          if (maxValue < max) maxValue = max;

          result[measureName].push({
            values: item[measureName].map((v) => v.value),
            datetimes: item[measureName].map((v) => v.datetime),
            pk: pkList[i],
          });
        }
        if (Math.abs(minValue) > maxValue && measureName === "strain") {
          maxValue = Math.abs(minValue);
        }
        minsWaterfall[measureName] = minValue;
        maxsWaterfall[measureName] = maxValue;
      }
    }

    setMinsWaterfall(minsWaterfall);
    setMaxsWaterfall(maxsWaterfall);
    setTemporalDataList(result);
  } catch (error) {
    toast.error(error.message, { className: "toast-error" });
  }
  setLoading(false);
};

export const IconDisplay = ({ filename, fileIndex }) => {
  const extension = obtainExtension(filename);
  const type = fileType(extension);

  if (type === "image") {
    return <PhotoIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "video") {
    return <OndemandVideoIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "audio") {
    return <MusicNoteIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (type === "compressed") {
    return <FolderZipIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
  if (filename.endsWith(".pdf")) {
    return <PictureAsPdfIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  } else {
    return <InsertDriveFileIcon id={`display_${fileIndex}_${type}`} sx={{ cursor: "pointer", margin: "1%" }} />;
  }
};
function obtainExtension(file) {
  const parts = file.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function fileType(extension) {
  for (const type in fileTypes) {
    if (fileTypes[type].includes(extension)) {
      return type;
    }
  }
  return null;
}
const fileTypes = {
  image: ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "tiff"],
  video: ["mp4", "avi", "mkv", "mov", "wmv", "flv", "webm", "mpeg"],
  sound: ["mp3", "wav", "ogg", "flac", "aac", "m4a", "wma"],
  compressed: ["zip", "rar", "7z", "tar", "gz", "bz2"],
};

export const reportFiling = async ({ id, setReport, setReportImages, setReportFiles, setLoading }) => {
  const reportResult = await getReport(id); //reportID
  const images = [];
  const otherFiles = [];
  //aqui hay que ordenar los archivos por imagenes primero y luego lo demás
  for (let indexFile = 0; indexFile < reportResult.data.data.attachments.length; indexFile++) {
    const extension = obtainExtension(reportResult.data.data.attachments[indexFile].filename);
    const imageChecker = fileType(extension, ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "tiff"]);
    if (imageChecker === "image") {
      images.push(reportResult.data.data.attachments[indexFile]);
    } else {
      otherFiles.push(reportResult.data.data.attachments[indexFile]);
    }
  }
  setReport(reportResult.data.data);
  setReportImages(images);
  setReportFiles(otherFiles);
  setLoading(false);
};

export const getPointsMap = async ({ assetsPoints, asset, dispatch, setAssetsPointsData }) => {
  const alreadyPoints = assetsPoints.data;
  if (!isNil(alreadyPoints)) {
    const puntosExistentes = (alreadyPoints, asset) => {
      return Object.keys(alreadyPoints).some((key) => key.startsWith(asset));
    };
    if (!puntosExistentes(alreadyPoints, asset) && !isNil(asset)) {
      const response = await getPointsByAssets(asset);
      const pointsMap = {};
      for (let index = 0; index < response.data.data.length; index++) {
        pointsMap[`${asset}${index + 1}`] = response.data.data[index];
      }
      dispatch(setAssetsPointsData(pointsMap));
    }
  }
};

export const manageAlertsAndReports = async ({
  assetAlerts,
  modulesEvents,
  setSelectedAlert,
  assetModule,
  actualEvent,
  dateTime,
  setAlertTimes,
  setAssetEvent,
  localParams,
  setEventAlerts,
  setReport,
  setReportImages,
  setReportFiles,
  setDate,
  setTime,
  setDateOptions,
  setTimeOptions,
  setDateOptionsModal,
  setTimeOptionsModal,
  setAlertAdditionalInfo,
  setSelectedAdditionalInfo,
  setChunk,
  setLoading,
}) => {
  setLoading(true);
  const dates = [];
  const times = [];
  const newAlerts = assetAlerts.filter((aa) => aa.package === actualEvent);
  for (let aaIndex = 0; aaIndex < newAlerts.length; aaIndex++) {
    const temp =
      newAlerts[aaIndex].dateStart.indexOf("T") !== -1
        ? newAlerts[aaIndex].dateStart.split("T")
        : newAlerts[aaIndex].dateStart.split(" ");
    if (dates.includes(temp[0])) {
      const indexDate = dates.indexOf(temp[0]);
      times[indexDate].push([temp[1], newAlerts[aaIndex].id]);
    } else {
      dates.push(temp[0]);
      times.push([[temp[1], newAlerts[aaIndex].id]]);
    }
  }

  if (!isEmpty(dates)) {
    const actualAlert =
      !isNil(localParams) && !isNil(localParams.startPointIndex) && !isNil(dateTime)
        ? newAlerts.find(
            (aa) =>
              aa.dateStart === dateTime &&
              aa.startIndex === parseFloat(localParams.startPointIndex) &&
              aa.endIndex === parseFloat(localParams.endPointIndex),
          )
        : !isNil(dateTime)
          ? newAlerts.find((aa) => aa.dateStart === dateTime)
          : newAlerts[0];

    if (!isNil(actualAlert)) {
      const temp =
        actualAlert.dateStart.indexOf("T") !== -1 ? actualAlert.dateStart.split("T") : actualAlert.dateStart.split(" ");
      setDate(dates.indexOf(temp[0]));
      setTime(times[dates.indexOf(temp[0])].findIndex((item) => item[0] === temp[1] && item[1] === actualAlert.id));
      setDateOptions(dates);
      setAlertTimes(times);
      setTimeOptions(times[dates.indexOf(temp[0])]);

      //const temp =
      //  actualAlert.dateStart.indexOf("T") !== -1 ? actualAlert.dateStart.split("T") : actualAlert.dateStart.split(" ");
      //setDate(dates.indexOf(temp[0]));
      //setTime(times[dates.indexOf(temp[0])].indexOf(temp[1]));
      //setDateOptions(dates);
      //setAlertTimes(times);
      //setTimeOptions(times[dates.indexOf(temp[0])]);
      //if (!isNil(dates)) {
      //  setDateOptionsModal(dates);
      //}
      //setTimeOptionsModal(times[dates.indexOf(temp[0])]);
    }
    //if (!isEmpty(dates) && !isEmpty(times)) {
    if (!isEmpty(dates) && !isEmpty(times)) {
      setSelectedAlert(actualAlert);
      const newAdditionalInfo = await getAlertAdditionalInfo(actualAlert.id);
      setAlertAdditionalInfo(newAdditionalInfo.data.data); // esto se setea para conseguir los chunks
      if (!isEmpty(newAdditionalInfo.data.data)) {
        setSelectedAdditionalInfo(newAdditionalInfo.data.data[0].id);
        const newChunk = await getChunkInfo(newAdditionalInfo.data.data[0].id);
        setChunk(newChunk.data.data);
      } else {
        setSelectedAdditionalInfo(null);
        setChunk([]);
      }

      let reportIndex = null;
      if (!isNil(actualAlert.reports)) {
        for (let alertRepIndex = 0; alertRepIndex < actualAlert.reports.length; alertRepIndex++) {
          if (!isNil(actualAlert.reports[alertRepIndex]) && !isEmpty(actualAlert.reports[alertRepIndex])) {
            reportIndex = alertRepIndex;
            break;
          }
        }
      }
      setEventAlerts(newAlerts);
      if (!isNil(reportIndex)) {
        reportFiling({
          id: actualAlert.reports[reportIndex].id,
          setReport,
          setReportImages,
          setReportFiles,
          setLoading,
        });
      } else {
        setReport();
        setReportImages();
        setReportFiles();
        setLoading(false);
      }
    }
  } else {
    setDate([]);
    setTime([]);
    setDateOptions([]);
    setAlertTimes([]);
    setTimeOptions([]);
    setDateOptionsModal([]);
    setTimeOptionsModal([]);
    setReport(null);
    setReportFiles(null);
    setLoading(false);
  }
};

export const getCurrentIntensityData = async ({
  data,
  setIntesityData,
  asset,
  assetInfo,
  assetSensors,
  sensorCats,
  catRefs,
  equipment,
  selectedEquipment,
  channel,
  selectedSub,
  startDate,
  afterAlert,
  dateTimeList,
  startIndex,
  endIndex,
  period = 30,
  maximum = 1000,
  setLoadingIntensity,
}) => {
  setLoadingIntensity(true);
  const allSensorIDs = [];
  const allSensorIDsMeasurands = [];
  const response = [];
  const actualSub = assetInfo.substructures.find((sub) => sub.ref === selectedSub.ref);
  const actualSensors = actualSub.sensors;
  for (let indexSensor = 0; indexSensor < assetSensors.length; indexSensor++) {
    if (actualSensors.includes(assetSensors[indexSensor].sensor_ID)) {
      for (let indexMeasurand = 0; indexMeasurand < assetSensors[indexSensor].measurands.length; indexMeasurand++) {
        let check = false;
        for (let indexCat = 0; indexCat < sensorCats.length; indexCat++) {
          if (catRefs[sensorCats[indexCat]].includes(assetSensors[indexSensor].measurands[indexMeasurand]))
            check = true;
          if (check) break;
        }
        if (check) {
          allSensorIDs.push(assetSensors[indexSensor].sensor_ID);
          allSensorIDsMeasurands.push([assetSensors[indexSensor].measurands[indexMeasurand]]);
          const addition = await getAssetPointsAdditionalData(
            asset,
            dateTimeList[0][0],
            assetSensors[indexSensor].measurands[indexMeasurand], //medida
            equipment,
            period,
            maximum,
            channel,
            startDate,
            afterAlert,
            assetSensors[indexSensor].sensor_ID, //ID
            startIndex,
            endIndex,
          );
          response.push(addition);
        }
      }
    }
  }
  const result = [];
  let minValue = Infinity;
  let maxValue = Number.NEGATIVE_INFINITY;
  const pkList = data?.[0].pk;
  for (let index = 0; index < response.length; index++) {
    if (!isNil(response[index]?.status) && response[index].status === 200 && !isNil(response[index].data)) {
      const data = response[index].data?.data;
      if (!isNil(data.data)) {
        for (let i = 0, j = startIndex; i < data.data.length; i++, j++) {
          for (let indexMeasurand = 0; indexMeasurand < allSensorIDsMeasurands[index].length; indexMeasurand++) {
            const item = data.data[i];
            if (!isNil(item[allSensorIDsMeasurands[index][indexMeasurand]])) {
              const values = item[allSensorIDsMeasurands[index][indexMeasurand]].map((item) => item.value);
              const min = Math.min(...values);
              const max = Math.max(...values);
              if (minValue > min) minValue = min;
              if (maxValue < max) maxValue = max;
              result.push({
                sensor_ID: allSensorIDs[index],
                values: item[allSensorIDsMeasurands[index][indexMeasurand]].map((v) => v.value),
                datetimes: item[allSensorIDsMeasurands[index][indexMeasurand]].map((v) => v.datetime),
                pk: pkList[i],
                measurand: allSensorIDsMeasurands[index][indexMeasurand],
              });
              if (Math.abs(minValue) > maxValue && allSensorIDsMeasurands[index][indexMeasurand] === "strain") {
                maxValue = Math.abs(minValue);
              }
            }
          }
        }
      }
    }
  }
  setIntesityData(result);
  setLoadingIntensity(false);
};

export const loadDoBfile = ({ event, data, setData, measurandsFiltered, channel, date }) => {
  if (!isNil(event)) {
    const file = event;
    const extension = file.name.split(".").pop().toLowerCase();
    const reader = new FileReader();

    reader.onload = (e) => {
      let read;

      try {
        if (extension === "json") {
          const text = e.target.result;
          read = JSON.parse(text);
        } else if (extension === "csv") {
          read = parseCSV(e.target.result);
        } else {
          throw new Error("unsupported.format");
        }
      } catch (err) {
        console.error(err);
        //setError("bad.json");
        return;
      }

      if (!read) {
        //setError("bad.structure");
        return;
      }

      try {
        let newData = clone(data);
        let lastIndexDoB = -1;
        for (let indexData = 0; indexData < data.length; indexData++) {
          if (!isNil(newData[indexData].DoB)) lastIndexDoB = indexData;
        }
        if (lastIndexDoB + 1 === data.length) newData.push({});
        const measurands = measurandsFiltered.filter((measurand) => measurand !== "DoB" && !measurand.includes("dob"));
        const newDob = {
          datetime: date + "T",
          values: read.map((element) => -element.dob),
          channel: channel,
          coordinates: read.map((element) => [element.latitude, element.longitude]),
          pk: read.map((element) => element.pk * 1000),
          seabed: read.map((element) => -element.seabed),
        };
        for (let indexData = 0; indexData < newData.length; indexData++) {
          if (isNil(newData[indexData])) {
            let newObject = {
              datetime: !isNil(newData[indexData].date) ? newData[indexData].date : "",
              pk: !isNil(newData[indexData].pk) ? newData[indexData].pk : null,
              DoB: newDob,
            };
            if (!isEmpty(measurands)) {
              for (let indexMeasurand = 0; indexMeasurand < measurands.length; indexMeasurand++) {
                newObject[measurands[indexMeasurand]] = !isNil(newData[indexData][measurands[indexMeasurand]])
                  ? newData[indexData][measurands[indexMeasurand]]
                  : null;
              }
            }
            newData.push(newObject);
          } else if (isNil(newData[indexData].DoB)) {
            let newObject = newData[indexData];
            newObject.date = !isNil(newData[indexData].date) ? newData[indexData].date : "";
            newObject.pk = !isNil(newData[indexData].pk) ? newData[indexData].pk : null;
            newObject.DoB = newDob;
            if (!isEmpty(measurands)) {
              for (let indexMeasurand = 0; indexMeasurand < measurands.length; indexMeasurand++) {
                newObject[measurands[indexMeasurand]] = !isNil(newData[indexData][measurands[indexMeasurand]])
                  ? newData[indexData][measurands[indexMeasurand]]
                  : null;
              }
            }
            newData[indexData] = newObject;
          }
        }
        setData(newData);
      } catch (error) {
        console.error(error);
        //setError("bad.structure");
      }
    };

    reader.readAsText(file);
  }
};
